import { SpriteSheet, Animation, range, AnimationStrategy } from 'excalibur';
import { Spritesheets } from './resources';
import { getRandomThing } from '@game-libs/exc-libs';

const dragonSpritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.dragon,
  grid: {
    rows: 1,
    columns: 34,
    spriteWidth: 134,
    spriteHeight: 82,
  },
});

const bigDragonSpritesheetIdle = SpriteSheet.fromImageSource({
  image: Spritesheets.dragon_big_idle,
  grid: {
    rows: 1,
    columns: 24,
    spriteWidth: 450,
    spriteHeight: 260,
  },
});

const bigDragonSpritesheetFire = SpriteSheet.fromImageSource({
  image: Spritesheets.dragon_big_fire,
  grid: {
    rows: 1,
    columns: 28,
    spriteWidth: 450,
    spriteHeight: 260,
  },
});

const bigDragonSpritesheetRoar = SpriteSheet.fromImageSource({
  image: Spritesheets.dragon_big_roar,
  grid: {
    rows: 1,
    columns: 28,
    spriteWidth: 450,
    spriteHeight: 260,
  },
});

export const dragonAnimation = {
  idleLeft: Animation.fromSpriteSheet(dragonSpritesheet, range(0, 7), 200),
  idleCenter: Animation.fromSpriteSheet(dragonSpritesheet, range(8, 15), 200),
  idleRight: Animation.fromSpriteSheet(dragonSpritesheet, range(16, 23), 200),
  roar: Animation.fromSpriteSheet(dragonSpritesheet, range(24, 34), 200),
  dying: Animation.fromSpriteSheet(dragonSpritesheet, range(30, 31), 150),
};

export const bigDragonAnimation = {
  idle: Animation.fromSpriteSheet(bigDragonSpritesheetIdle, range(0, 23), 200),
  fireBreath: Animation.fromSpriteSheet(bigDragonSpritesheetFire, range(0, 27), 200),
  roar: Animation.fromSpriteSheet(bigDragonSpritesheetRoar, range(0, 27), 150),
  dying: Animation.fromSpriteSheet(bigDragonSpritesheetRoar, range(0, 1), 150),
};



const birdSpritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.bird,
  grid: {
    rows: 1,
    columns: 7,
    spriteWidth: 16,
    spriteHeight: 16,
  },
});

export const birdAnimation = Animation.fromSpriteSheet(birdSpritesheet, range(0, 3), 100);

export const birdExplodingAnimation = Animation.fromSpriteSheet(
  birdSpritesheet,
  range(4, 6),
  100,
  AnimationStrategy.End,
);

const getUnitSpritesheet = (unit: string) => {
  const width = Spritesheets[unit].width;
  const height = Spritesheets[unit].height;

  return SpriteSheet.fromImageSource({
    image: Spritesheets[unit],
    grid: {
      rows: 1,
      columns: Math.floor(width / height),
      spriteWidth: 8,
      spriteHeight: 8,
    },
  });
};

export const getUnitAnimations = (unit: string) => {
  if (unit === 'catapult') {
    return getCatapultAnimation();
  }

  const stunnedLength = 3;
  const columns = getUnitSpritesheet(unit).columns;

  const mainLoop = range(1, columns - (stunnedLength + 1));
  const stunnedLoop = range(columns - stunnedLength, columns - 1);

  return {
    idle: Animation.fromSpriteSheet(getUnitSpritesheet(unit), [0], 1000),
    attack: Animation.fromSpriteSheet(getUnitSpritesheet(unit), mainLoop, 100),
    stunned: Animation.fromSpriteSheet(getUnitSpritesheet(unit), stunnedLoop, 100),
  };
};

const getCatapultAnimation = () => {
  const width = Spritesheets['catapult'].width;
  const height = Spritesheets['catapult'].height;

  const spritesheet = SpriteSheet.fromImageSource({
    image: Spritesheets['catapult'],
    grid: {
      rows: 1,
      columns: Math.floor(width / height),
      spriteWidth: 32,
      spriteHeight: 32,
    },
  });

  return {
    idle: Animation.fromSpriteSheet(spritesheet, [0], 1000),
    // attack: Animation.fromSpriteSheet(spritesheet, range(0, 5), 200),
    stunned: Animation.fromSpriteSheet(spritesheet, [0], 100),
    attack: Animation.fromSpriteSheetCoordinates({
      spriteSheet: spritesheet,
      frameCoordinates: [
        { x: 0, y: 0, duration: 1700 },
        { x: 1, y: 0, duration: 50 },
        { x: 2, y: 0, duration: 50 },
        { x: 3, y: 0, duration: 400 },
        { x: 4, y: 0, duration: 400 },
        { x: 5, y: 0, duration: 400 },
      ],
    }),
  };
};

// const anim = Animation.fromSpriteSheetCoordinates({
//   spriteSheet,
//   frameCoordinates: [
//     {x: 0, y: 5, duration: 100, options { flipHorizontal: true }},
//     {x: 1, y: 5, duration: 200},
//     {x: 2, y: 5},
//     {x: 3, y: 5}
//   ],
//   strategy: AnimationStrategy.PingPong
//  });

const criticalHitSpritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.critical_hit,
  grid: {
    rows: 1,
    columns: 7,
    spriteWidth: 48,
    spriteHeight: 48,
  },
});

const explosionSpritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.explosion,
  grid: {
    rows: 1,
    columns: 13,
    spriteWidth: 80,
    spriteHeight: 48,
  },
});

export const deathAnimation = Animation.fromSpriteSheet(explosionSpritesheet, range(2, 12), 70, AnimationStrategy.End);

const hit1Spritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.hit_1,
  grid: {
    rows: 1,
    columns: 5,
    spriteWidth: 64,
    spriteHeight: 64,
  },
});

const hit2Spritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.hit_2,
  grid: {
    rows: 1,
    columns: 4,
    spriteWidth: 72,
    spriteHeight: 64,
  },
});

const randomHits = [
  { value: hit1Spritesheet, probability: 50 },
  { value: hit2Spritesheet, probability: 50 },
];

export const hitEffectAnimation = {
  normal: () => Animation.fromSpriteSheet(getRandomThing(randomHits), range(0, 4), 50, AnimationStrategy.End),
  critical: Animation.fromSpriteSheet(criticalHitSpritesheet, range(0, 7), 50, AnimationStrategy.End),
};

// SMOKE
const smokeSpritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.smoke,
  grid: {
    rows: 1,
    columns: 5,
    spriteWidth: 78,
    spriteHeight: 32,
  },
});
export const smokeAnimation = Animation.fromSpriteSheet(smokeSpritesheet, range(0, 4), 50, AnimationStrategy.End);

// ARRIVAL SMOKE
const arrivalSmokeSpritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.arrival_smoke,
  grid: {
    rows: 1,
    columns: 27,
    spriteWidth: 70,
    spriteHeight: 64,
  },
});

export const arrivalSmokeAnimation = Animation.fromSpriteSheet(arrivalSmokeSpritesheet, range(0, 16), 50, AnimationStrategy.End);

// LIGHTNING
const lightningSpritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.lightning,
  grid: {
    rows: 1,
    columns: 15,
    spriteWidth: 111,
    spriteHeight: 159,
  },
});

export const lightningAnimation = Animation.fromSpriteSheet(
  lightningSpritesheet,
  range(0, 14),
  40,
  AnimationStrategy.End,
);

// POISON
const poisonSpritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.poison,
  grid: {
    rows: 1,
    columns: 6,
    spriteWidth: 48,
    spriteHeight: 48,
  },
});

export const poisonAnimation = Animation.fromSpriteSheet(poisonSpritesheet, range(0, 5), 40, AnimationStrategy.End);

// DUMMY TARGET
const dummySpritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.dummy,
  grid: {
    rows: 3,
    columns: 8,
    spriteWidth: 32,
    spriteHeight: 32,
  },
});

export const dummyAnimation = {
  idle: Animation.fromSpriteSheet(dummySpritesheet, [0], 0, AnimationStrategy.Freeze),
  weak_hit: Animation.fromSpriteSheet(dummySpritesheet, range(0, 3), 100, AnimationStrategy.End),
  mid_hit: Animation.fromSpriteSheet(dummySpritesheet, range(8, 12), 100, AnimationStrategy.End),
  hard_hit: Animation.fromSpriteSheet(dummySpritesheet, range(16, 23), 100, AnimationStrategy.End),
};

// KING
const kingSpritesheet = SpriteSheet.fromImageSource({
  image: Spritesheets.king,
  grid: {
    rows: 1,
    columns: 2,
    spriteWidth: 8,
    spriteHeight: 8,
  },
});

export const kingAnimation = {
  idle: Animation.fromSpriteSheet(kingSpritesheet, [0], 0, AnimationStrategy.Freeze),
  shake_head: Animation.fromSpriteSheet(kingSpritesheet, [0, 1, 0, 1, 0, 1, 0, 1, 0,], 150, AnimationStrategy.Freeze),
};
